import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Search, Car, Plus, ChevronDown, Check, Calendar, Tag, AlertCircle, AlertTriangle, Trash2, Pencil, ArrowUpDown } from 'lucide-react';
import { API_URL } from '../config';
import Navbar from './Navbar';

const allProvinces = [
    "กรุงเทพมหานคร", "กระบี่", "กาญจนบุรี", "กาฬสินธุ์", "กำแพงเพชร", "ขอนแก่น", "จันทบุรี", "ฉะเชิงเทรา", "ชลบุรี", "ชัยนาท",
    "ชัยภูมิ", "ชุมพร", "เชียงราย", "เชียงใหม่", "ตรัง", "ตราด", "ตาก", "นครนายก", "นครปฐม", "นครพนม",
    "นครราชสีมา", "นครศรีธรรมราช", "นครสวรรค์", "นนทบุรี", "นราธิวาส", "น่าน", "บึงกาฬ", "บุรีรัมย์", "ปทุมธานี", "ประจวบคีรีขันธ์",
    "ปราจีนบุรี", "ปัตตานี", "พระนครศรีอยุธยา", "พะเยา", "พังงา", "พัทลุง", "พิจิตร", "พิษณุโลก", "เพชรบุรี", "เพชรบูรณ์",
    "แพร่", "ภูเก็ต", "มหาสารคาม", "มุกดาหาร", "แม่ฮ่องสอน", "ยโสธร", "ยะลา", "ร้อยเอ็ด", "ระนอง", "ระยอง",
    "ราชบุรี", "ลพบุรี", "ลำปาง", "ลำพูน", "เลย", "ศรีสะเกษ", "สกลนคร", "สงขลา", "สตูล", "สมุทรปราการ",
    "สมุทรสงคราม", "สมุทรสาคร", "สระแก้ว", "สระบุรี", "สิงห์บุรี", "สุโขทัย", "สุพรรณบุรี", "สุราษฎร์ธานี", "สุรินทร์", "หนองคาย",
    "หนองบัวลำภู", "อ่างทอง", "อำนาจเจริญ", "อุดรธานี", "อุตรดิตถ์", "อุทัยธานี", "อุบลราชธานี"
];

const CustomFilterSelect = ({ label, value, onChange, options, allLabel = "ทั้งหมด", icon: Icon }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedOption = options.find(opt => String(opt.value) === String(value));

    return (
        <div className="w-full md:w-auto min-w-[140px] relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full bg-white border rounded-lg shadow-sm flex items-center px-2.5 py-1.5 gap-2 text-left ${isOpen ? '' : ''
                    }`}
            >
                {Icon && (
                    <div className={`p-1 rounded-lg flex-shrink-0 transition-colors ${isOpen ? 'bg-blue-100' : 'bg-blue-50'}`}>
                        <Icon className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                )}
                <div className="flex flex-col w-full min-w-0">
                    <span className="text-[8px] text-slate-500 font-bold uppercase tracking-wider leading-none mb-1">{label}</span>
                    <span className="text-xs font-bold text-slate-800">
                        {selectedOption?.label || allLabel}
                    </span>
                </div>
                <div className={`text-slate-300 transition-transform duration-200 ${isOpen ? 'rotate-180 text-blue-500' : ''}`}>
                    <ChevronDown className="h-3 w-3" />
                </div>
            </button>

            {isOpen && (
                <div className="absolute z-[60] mt-1.5 min-w-full w-max max-w-[240px] left-0 bg-white border border-slate-100 rounded-[1.5rem] shadow-2xl py-1.5 overflow-hidden animate-in fade-in zoom-in-95 duration-200 origin-top">
                    <button
                        onClick={() => { onChange(''); setIsOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-[11px] font-bold flex items-center justify-between transition-colors ${!value || value === 'All' ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <span>{allLabel}</span>
                        {(!value || value === 'All') && <Check className="w-3.5 h-3.5" />}
                    </button>
                    <div className="h-px bg-slate-100 my-1" />
                    <div className="max-h-[200px] overflow-y-auto scrollbar-hide">
                        {options.map((opt, idx) => (
                            <button
                                key={idx}
                                onClick={() => { onChange(opt.value); setIsOpen(false); }}
                                className={`w-full text-left px-4 py-2 text-[11px] font-bold flex items-center justify-between transition-colors ${String(value) === String(opt.value) ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                            >
                                <span className="truncate">{opt.label}</span>
                                {String(value) === String(opt.value) && <Check className="w-3.5 h-3.5" />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const ResidentManagement = ({ token, role, username, setToken, permissions = [] }) => {
    const [vehicles, setVehicles] = useState([]);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filterStickerYear, setFilterStickerYear] = useState('');
    const [filterVehicleType, setFilterVehicleType] = useState('All'); // 'All', 'Car', 'Motorcycle'
    const [filterStatus, setFilterStatus] = useState('All');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [uniqueYears, setUniqueYears] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Modals
    const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);

    // Forms
    const [vehicleForm, setVehicleForm] = useState({
        room_number: '234/',
        name: '',
        phone_number: '',
        license_plate: '',
        plate_category: 'Normal', // 'Normal', 'RedPlate', 'NoPlate'
        province: '',
        brand: '',
        color: '',
        vehicle_type: 'Car',
        sticker_number: '',
        sticker_year: '',
        sticker_id: '',
        remark: '',
        is_active: true
    });

    const [isProvinceOpen, setIsProvinceOpen] = useState(false);
    const [provinceSearch, setProvinceSearch] = useState('');
    const provinceInputRef = useRef(null);
    const [dropdownStyle, setDropdownStyle] = useState({});

    const [editingVehicleId, setEditingVehicleId] = useState(null);

    // Dropdown states
    const [isYearOpen, setIsYearOpen] = useState(false);
    const [isTypeOpen, setIsTypeOpen] = useState(false);
    const [isPlateCategoryOpen, setIsPlateCategoryOpen] = useState(false);
    const [isStatusOpen, setIsStatusOpen] = useState(false);
    const [isRowsDropdownOpen, setIsRowsDropdownOpen] = useState(false);

    const [error, setError] = useState('');
    const [refreshToggle, setRefreshToggle] = useState(0);

    const isGenError = error === 'กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วนก่อนกด Gen Sticker No.' || error === 'กรุณากรอกเลขที่บ้านต่อจาก 234/ ให้ครบถ้วน';

    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [vehicleToDelete, setVehicleToDelete] = useState(null);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const lastFetchRef = useRef({ paramsStr: '', token: '', refreshToggle: -1 });
    const lastFiltersRef = useRef({ debouncedSearch, filterStickerYear, filterVehicleType, filterStatus, rowsPerPage, sortConfigKey: sortConfig.key, sortConfigDir: sortConfig.direction });

    // Consolidated effect for fetching and page reset
    useEffect(() => {
        const params = {
            searchTerm: debouncedSearch,
            stickerYear: filterStickerYear,
            vehicleType: filterVehicleType,
            status: filterStatus,
            page: currentPage,
            limit: rowsPerPage,
            sortBy: sortConfig.key,
            order: sortConfig.direction === 'asc' ? 'ASC' : 'DESC'
        };
        const paramsStr = JSON.stringify(params);

        // 1. Handle Page Reset on Filter Change
        const filtersChanged =
            lastFiltersRef.current.debouncedSearch !== debouncedSearch ||
            lastFiltersRef.current.filterStickerYear !== filterStickerYear ||
            lastFiltersRef.current.filterVehicleType !== filterVehicleType ||
            lastFiltersRef.current.filterStatus !== filterStatus ||
            lastFiltersRef.current.rowsPerPage !== rowsPerPage ||
            lastFiltersRef.current.sortConfigKey !== sortConfig.key ||
            lastFiltersRef.current.sortConfigDir !== sortConfig.direction;

        if (filtersChanged) {
            lastFiltersRef.current = {
                debouncedSearch,
                filterStickerYear,
                filterVehicleType,
                filterStatus,
                rowsPerPage,
                sortConfigKey: sortConfig.key,
                sortConfigDir: sortConfig.direction
            };
            if (currentPage !== 1) {
                setCurrentPage(1);
                return;
            }
        }

        // 2. Avoid Redundant Calls (e.g. React StrictMode or token updates)
        // 2. Perform the Fetch
        const controller = new AbortController();
        const signal = controller.signal;
        lastFetchRef.current = { paramsStr, token, refreshToggle };

        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(`${API_URL}/api/all-vehicles`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params,
                    signal
                });

                if (response.data && !signal.aborted) {
                    setVehicles(response.data.vehicles || []);
                    setTotalItems(response.data.total || 0);
                    setTotalPages(response.data.totalPages || 0);
                    setUniqueYears(response.data.uniqueYears || []);
                }
            } catch (err) {
                if (axios.isCancel(err) || signal.aborted) return;
                console.error('Failed to fetch vehicles', err);
            } finally {
                if (!signal.aborted) {
                    setIsLoading(false);
                }
            }
        };

        const timeoutId = setTimeout(() => {
            fetchData();
        }, 50);

        return () => {
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [token, debouncedSearch, filterStickerYear, filterVehicleType, filterStatus, currentPage, rowsPerPage, refreshToggle, sortConfig]);

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-20" />;
        return sortConfig.direction === 'asc' ?
            <ChevronDown className="w-3 h-3 ml-1 text-indigo-600 rotate-180" /> :
            <ChevronDown className="w-3 h-3 ml-1 text-indigo-600" />;
    };

    // currentVehicles is now just the vehicles from the server
    const currentVehicles = vehicles;

    // Vehicle Handlers
    const handleSaveVehicle = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        try {
            const requiredFields = [
                'sticker_year', 'vehicle_type', 'sticker_id', 'room_number',
                'sticker_number', 'name', 'phone_number'
            ];

            if (vehicleForm.plate_category === 'Normal' || vehicleForm.plate_category === 'RedPlate') {
                requiredFields.push('license_plate', 'province');
            }

            const missingFields = requiredFields.filter(field => !vehicleForm[field]);

            if (missingFields.length > 0) {
                const fieldLabels = {
                    sticker_year: 'ปีสติ๊กเกอร์',
                    vehicle_type: 'ประเภทรถ',
                    sticker_id: 'Sticker ID',
                    room_number: 'บ้านเลขที่',
                    sticker_number: 'Sticker No.',
                    name: 'ชื่อผู้พักอาศัย',
                    phone_number: 'เบอร์โทรศัพท์',
                    license_plate: 'ทะเบียนรถ',
                    province: 'จังหวัด'
                };
                const missingLabels = missingFields.map(f => fieldLabels[f]);
                setError(`กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วนก่อนกดบันทึก (${missingLabels.join(', ')})`);
                return;
            }

            // Province validation to ensure it matches the list
            if (vehicleForm.plate_category === 'Normal' || vehicleForm.plate_category === 'RedPlate') {
                if (!allProvinces.includes(vehicleForm.province)) {
                    setError('กรุณาเลือกจังหวัดจากรายการที่กำหนดเท่านั้น');
                    return;
                }
            }

            // Room number validation: must be 234/xxx
            if (vehicleForm.room_number === '234/' || !vehicleForm.room_number.startsWith('234/')) {
                setError('กรุณากรอกเลขที่บ้านต่อจาก 234/ ให้ครบถ้วน');
                return;
            }

            // Sticker ID validation: exactly 3 digits and 001-999
            if (!/^[0-9]{3}$/.test(vehicleForm.sticker_id) || vehicleForm.sticker_id === '000') {
                setError('Sticker ID ต้องเป็นตัวเลข 3 หลัก ระหว่าง 001-999');
                return;
            }

            // Phone Number validation: exactly 10 digits
            if (vehicleForm.phone_number && vehicleForm.phone_number.length !== 10) {
                setError('เบอร์โทรศัพท์ต้องมี 10 หลัก');
                return;
            }

            let finalLicensePlate = vehicleForm.license_plate;
            if (vehicleForm.plate_category === 'RedPlate' && !vehicleForm.license_plate) {
                finalLicensePlate = 'ป้ายแดง';
            } else if (vehicleForm.plate_category === 'NoPlate' && !vehicleForm.license_plate) {
                finalLicensePlate = 'ยังไม่มีทะเบียน';
            }

            const vehicleData = {
                ...vehicleForm,
                license_plate: finalLicensePlate,
                plate_category: vehicleForm.plate_category,
                // Ensure optional fields are sent as strings if they are empty
                sticker_id: vehicleForm.sticker_id || '',
                remark: vehicleForm.remark || ''
            };

            if (editingVehicleId) {
                // Check if another record has the same room and year
                const isDuplicate = vehicles.some(v =>
                    v.room_number === vehicleForm.room_number &&
                    v.sticker_year.toString() === vehicleForm.sticker_year.toString() &&
                    v.id !== editingVehicleId
                );
                if (isDuplicate) {
                    setError(`ห้อง ${vehicleForm.room_number} มีข้อมูลสติ๊กเกอร์ของปี ${vehicleForm.sticker_year} อยู่แล้ว`);
                    return;
                }

                // Check if another record has the same Sticker ID, Year and Type
                const isStickerDuplicate = vehicles.some(v =>
                    v.sticker_id === vehicleForm.sticker_id &&
                    v.sticker_year.toString() === vehicleForm.sticker_year.toString() &&
                    v.vehicle_type === vehicleForm.vehicle_type &&
                    v.id !== editingVehicleId
                );
                if (isStickerDuplicate) {
                    const typeLabel = vehicleForm.vehicle_type === 'Car' ? 'รถยนต์' : (vehicleForm.vehicle_type === 'Motorcycle' ? 'รถจักรยานยนต์' : vehicleForm.vehicle_type);
                    setError(`Sticker ID ${vehicleForm.sticker_id} สำหรับรถประเภท${typeLabel} ปี ${vehicleForm.sticker_year} ถูกใช้งานแล้ว`);
                    return;
                }

                // Check if another record has the same Sticker No. and Year
                const isStickerNoDuplicate = vehicles.some(v =>
                    v.sticker_number === vehicleForm.sticker_number &&
                    v.sticker_year.toString() === vehicleForm.sticker_year.toString() &&
                    v.id !== editingVehicleId
                );
                if (isStickerNoDuplicate) {
                    setError(`Sticker No. ${vehicleForm.sticker_number} ปี ${vehicleForm.sticker_year} ถูกใช้งานในระบบแล้ว`);
                    return;
                }

                await axios.put(`${API_URL}/api/vehicles/${editingVehicleId}`, vehicleData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage('อัปเดตข้อมูลเรียบร้อยแล้ว');
                setShowSuccessPopup(true);
            } else {
                // Check if any record has the same room and year
                const isDuplicate = vehicles.some(v =>
                    v.room_number === vehicleForm.room_number &&
                    v.sticker_year.toString() === vehicleForm.sticker_year.toString()
                );
                if (isDuplicate) {
                    setError(`ห้อง ${vehicleForm.room_number} มีข้อมูลสติ๊กเกอร์ของปี ${vehicleForm.sticker_year} อยู่แล้ว`);
                    return;
                }

                // Check if any record has the same Sticker ID, Year and Type
                const isStickerDuplicate = vehicles.some(v =>
                    v.sticker_id === vehicleForm.sticker_id &&
                    v.sticker_year.toString() === vehicleForm.sticker_year.toString() &&
                    v.vehicle_type === vehicleForm.vehicle_type
                );
                if (isStickerDuplicate) {
                    const typeLabel = vehicleForm.vehicle_type === 'Car' ? 'รถยนต์' : (vehicleForm.vehicle_type === 'Motorcycle' ? 'รถจักรยานยนต์' : vehicleForm.vehicle_type);
                    setError(`Sticker ID ${vehicleForm.sticker_id} สำหรับรถประเภท${typeLabel} ปี ${vehicleForm.sticker_year} ถูกใช้งานแล้ว`);
                    return;
                }

                // Check if any record has the same Sticker No. and Year
                const isStickerNoDuplicate = vehicles.some(v =>
                    v.sticker_number === vehicleForm.sticker_number &&
                    v.sticker_year.toString() === vehicleForm.sticker_year.toString()
                );
                if (isStickerNoDuplicate) {
                    setError(`Sticker No. ${vehicleForm.sticker_number} ปี ${vehicleForm.sticker_year} ถูกใช้งานในระบบแล้ว`);
                    return;
                }

                await axios.post(`${API_URL}/api/vehicles`, vehicleData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage('เพิ่มข้อมูลเรียบร้อยแล้ว');
                setShowSuccessPopup(true);
            }
            setIsVehicleModalOpen(false);
            setRefreshToggle(prev => prev + 1);
            setVehicleForm({
                room_number: '234/',
                name: '',
                phone_number: '',
                license_plate: '',
                plate_category: 'Normal',
                province: '',
                brand: '',
                color: '',
                vehicle_type: 'Car',
                sticker_number: '',
                sticker_year: new Date().getFullYear(),
                sticker_id: '',
                remark: '',
                is_active: true
            });
            setEditingVehicleId(null);
            setIsProvinceOpen(false);
            setProvinceSearch('');
        } catch (err) {
            if (err.response?.status === 401) return;
            console.error('Error saving vehicle:', err);
            setError(err.response?.data || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
        }
    };

    const handleDeleteVehicle = (id) => {
        setVehicleToDelete(id);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!vehicleToDelete) return;
        try {
            await axios.delete(`${API_URL}/api/vehicles/${vehicleToDelete}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSuccessMessage('ลบข้อมูลเรียบร้อยแล้ว');
            setShowSuccessPopup(true);
            setRefreshToggle(prev => prev + 1);
            setShowDeleteConfirm(false);
            setVehicleToDelete(null);
        } catch (err) {
            if (err.response?.status === 401) return;
            console.error('Error deleting vehicle:', err);
            setError(err.response?.data || 'เกิดข้อผิดพลาดในการลบข้อมูล');
            setShowDeleteConfirm(false);
        }
    };

    const openVehicleModal = (vehicle = null) => {
        setError('');
        setSuccessMessage('');
        if (vehicle) {
            setEditingVehicleId(vehicle.id);
            setVehicleForm({
                room_number: vehicle.room_number || '',
                name: vehicle.name || '',
                phone_number: vehicle.phone_number || '',
                license_plate: vehicle.license_plate,
                plate_category: vehicle.plate_category || (vehicle.license_plate === 'ป้ายแดง' ? 'RedPlate' : (vehicle.license_plate === 'ยังไม่มีทะเบียน' ? 'NoPlate' : 'Normal')),
                province: vehicle.province || '',
                brand: vehicle.brand || '',
                color: vehicle.color || '',
                vehicle_type: vehicle.vehicle_type || 'Car',
                sticker_number: vehicle.sticker_number || '',
                sticker_year: vehicle.sticker_year || new Date().getFullYear(),
                sticker_id: vehicle.sticker_id || '',
                remark: vehicle.remark || '',
                is_active: vehicle.is_active === 1 || vehicle.is_active === true
            });
            setProvinceSearch(vehicle.province || '');
        } else {
            setEditingVehicleId(null);
            setVehicleForm({
                room_number: '234/',
                name: '',
                phone_number: '',
                license_plate: '',
                plate_category: 'Normal',
                province: '',
                brand: '',
                color: '',
                vehicle_type: 'Car',
                sticker_number: '',
                sticker_year: new Date().getFullYear(),
                sticker_id: '',
                remark: '',
                is_active: true
            });
            setProvinceSearch('');
        }
        setIsVehicleModalOpen(true);
        setIsProvinceOpen(false);
    };

    const closeVehicleModal = () => {
        setIsVehicleModalOpen(false);
        setError('');
        setSuccessMessage('');
        setIsProvinceOpen(false);
    };

    const handleCloseProvinceDropdown = () => {
        setIsProvinceOpen(false);
        const trimmed = provinceSearch ? provinceSearch.trim() : '';
        if (trimmed) {
            // Check if there is an exact match
            const exactMatch = allProvinces.find(p => p === trimmed);
            if (exactMatch) {
                setVehicleForm(prev => ({ ...prev, province: exactMatch }));
                setProvinceSearch(exactMatch);
            } else {
                // Check if there is a unique partial match
                const matches = allProvinces.filter(p => p.includes(trimmed));
                if (matches.length === 1) {
                    setVehicleForm(prev => ({ ...prev, province: matches[0] }));
                    setProvinceSearch(matches[0]);
                } else {
                    // Revert/clear if not found
                    setVehicleForm(prev => ({ ...prev, province: '' }));
                    setProvinceSearch('');
                }
            }
        } else {
            setVehicleForm(prev => ({ ...prev, province: '' }));
            setProvinceSearch('');
        }
    };

    const updateDropdownPosition = useCallback(() => {
        if (provinceInputRef.current) {
            const rect = provinceInputRef.current.getBoundingClientRect();
            setDropdownStyle({
                position: 'fixed',
                bottom: window.innerHeight - rect.top + 5,
                left: rect.left,
                width: rect.width,
                maxHeight: '200px',
                zIndex: 9999
            });
        }
    }, []);

    useEffect(() => {
        if (isProvinceOpen) {
            updateDropdownPosition();
            window.addEventListener('scroll', updateDropdownPosition, true);
            window.addEventListener('resize', updateDropdownPosition);
        }
        return () => {
            window.removeEventListener('scroll', updateDropdownPosition, true);
            window.removeEventListener('resize', updateDropdownPosition);
        };
    }, [isProvinceOpen, updateDropdownPosition]);

    const menuPerm = permissions.find(p => p.role.toLowerCase() === role.toLowerCase() && p.menu_key === 'residents');
    const canEdit = menuPerm ? (menuPerm.can_edit === 1 || menuPerm.can_edit === true) : false;

    return (
        <div className="bg-slate-50 min-h-screen pb-10">
            <Navbar role={role} username={username} onLogout={() => setToken(null)} permissions={permissions} />

            <div className="max-w-7xl mx-auto px-4 md:px-8 space-y-6">

                {/* Success/Error Message */}
                {(!isVehicleModalOpen && error) && (
                    <div className="p-4 rounded-xl border flex items-center gap-3 animate-in slide-in-from-top-2 bg-red-50 border-red-200 text-red-700">
                        <span className="font-bold text-sm">{error}</span>
                        <button onClick={() => { setError(''); }} className="ml-auto hover:bg-black/5 p-1 rounded-full"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg></button>
                    </div>
                )}

                <div className="relative md:sticky md:top-[64px] z-40 bg-slate-50/80 pt-2 pb-3 mb-2 -mx-2 px-2 md:-mx-4 md:px-4">
                    <div className="bg-white p-3 sm:p-4 rounded-xl shadow-md border border-slate-200">
                        <div className="grid grid-cols-2 md:flex md:flex-wrap lg:flex-nowrap gap-2 sm:gap-4 items-center">
                            {/* Search */}
                            <div className="relative col-span-2 md:w-64">
                                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                    <Search className="w-4 h-4 text-slate-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="ค้นหา ID, Sticker, ทะเบียน..."
                                    className="w-full bg-white border border-slate-200 pl-10 pr-4 py-2 rounded-xl text-base md:text-[13px] text-slate-700 outline-none placeholder:text-slate-300"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* Filter Year */}
                            <div className="col-span-1 w-full md:w-auto">
                                <CustomFilterSelect
                                    label="ปีสติ๊กเกอร์"
                                    icon={Calendar}
                                    allLabel="ทั้งหมด"
                                    value={filterStickerYear}
                                    onChange={setFilterStickerYear}
                                    options={uniqueYears.map(year => ({ label: year.toString(), value: year }))}
                                />
                            </div>

                            {/* Filter Vehicle Type */}
                            <div className="col-span-1 w-full md:w-auto">
                                <CustomFilterSelect
                                    label="ประเภทรถ"
                                    icon={Car}
                                    allLabel="ทั้งหมด"
                                    value={filterVehicleType}
                                    onChange={setFilterVehicleType}
                                    options={[
                                        { label: 'รถยนต์', value: 'Car' },
                                        { label: 'จักรยานยนต์', value: 'Motorcycle' }
                                    ]}
                                />
                            </div>

                            {/* Filter Status */}
                            <div className="col-span-1 w-full md:w-auto">
                                <CustomFilterSelect
                                    label="สถานะ"
                                    icon={Tag}
                                    allLabel="ทั้งหมด"
                                    value={filterStatus}
                                    onChange={setFilterStatus}
                                    options={[
                                        { label: 'Active', value: 'Active' },
                                        { label: 'Inactive', value: 'Inactive' }
                                    ]}
                                />
                            </div>

                            <div className="col-span-1 md:w-auto md:ml-auto">
                                {canEdit && (
                                    <button
                                        onClick={() => openVehicleModal()}
                                        className="w-full md:w-auto flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] shadow-md shadow-blue-500/10 active:scale-95 border border-blue-500/20"
                                    >
                                        <Plus className="w-4 h-4 shrink-0" />
                                        <span className="truncate">เพิ่มข้อมูล</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>



                {/* Data Table */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                        <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            Resident Management
                        </h2>
                        <span className="text-[9px] font-bold text-slate-500 bg-slate-200 px-2 py-1 rounded-full">{totalItems} รายการ</span>
                    </div>

                    <div className="overflow-x-auto relative min-h-[400px]">
                        {isLoading && (
                            <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center backdrop-blur-[1px]">
                                <div className="flex flex-col items-center gap-3">
                                    <div className="w-10 h-10 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin"></div>
                                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-[0.2em] animate-pulse">Loading</span>
                                </div>
                            </div>
                        )}
                        <table className="w-full text-left border-collapse table-fixed min-w-[1200px]">
                            <thead>
                                <tr className="bg-slate-50/50">
                                    <th onClick={() => requestSort('sticker_year')} className="w-[45px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-center cursor-pointer hover:bg-slate-100/50 transition-colors">
                                        <div className="flex items-center justify-center">ปี {getSortIcon('sticker_year')}</div>
                                    </th>
                                    <th onClick={() => requestSort('vehicle_type')} className="w-[65px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-center cursor-pointer hover:bg-slate-100/50 transition-colors">
                                        <div className="flex items-center justify-center">ประเภท {getSortIcon('vehicle_type')}</div>
                                    </th>
                                    <th onClick={() => requestSort('sticker_id')} className="w-[60px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-center cursor-pointer hover:bg-slate-100/50 transition-colors">
                                        <div className="flex items-center justify-center">ID {getSortIcon('sticker_id')}</div>
                                    </th>
                                    <th onClick={() => requestSort('sticker_number')} className="w-[85px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-center cursor-pointer hover:bg-slate-100/50 transition-colors">
                                        <div className="flex items-center justify-center text-nowrap">Sticker No. {getSortIcon('sticker_number')}</div>
                                    </th>
                                    <th onClick={() => requestSort('license_plate')} className="w-[95px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-center cursor-pointer hover:bg-slate-100/50 transition-colors">
                                        <div className="flex items-center justify-center text-nowrap">ทะเบียนรถ {getSortIcon('license_plate')}</div>
                                    </th>
                                    <th onClick={() => requestSort('room_number')} className="w-[75px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-left cursor-pointer hover:bg-slate-100/50 transition-colors">
                                        <div className="flex items-center">บ้านเลขที่ {getSortIcon('room_number')}</div>
                                    </th>
                                    <th onClick={() => requestSort('phone_number')} className="w-[85px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-left cursor-pointer hover:bg-slate-100/50 transition-colors">
                                        <div className="flex items-center">เบอร์โทร {getSortIcon('phone_number')}</div>
                                    </th>
                                    <th onClick={() => requestSort('name')} className="w-[120px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-left cursor-pointer hover:bg-slate-100/50 transition-colors">
                                        <div className="flex items-center">ชื่อผู้พักอาศัย {getSortIcon('name')}</div>
                                    </th>
                                    <th className="w-[130px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter">รายละเอียดรถ</th>
                                    <th className="w-[110px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-left">หมายเหตุ</th>
                                    <th onClick={() => requestSort('created_at')} className="w-[105px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-left text-nowrap cursor-pointer hover:bg-slate-100/50 transition-colors">
                                        <div className="flex items-center">วันที่สร้าง {getSortIcon('created_at')}</div>
                                    </th>
                                    <th onClick={() => requestSort('updated_at')} className="w-[105px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-left text-nowrap cursor-pointer hover:bg-slate-100/50 transition-colors">
                                        <div className="flex items-center">วันที่อัปเดต {getSortIcon('updated_at')}</div>
                                    </th>
                                    <th onClick={() => requestSort('is_active')} className="w-[65px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-center cursor-pointer hover:bg-slate-100/50 transition-colors">
                                        <div className="flex items-center justify-center">สถานะ {getSortIcon('is_active')}</div>
                                    </th>
                                    <th className="w-[65px] px-1 py-3 text-[10px] font-bold text-slate-500 tracking-tighter text-right"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {vehicles.length === 0 && !isLoading ? (
                                    <tr>
                                        <td colSpan="14" className="p-8 text-center text-slate-500 bg-white">
                                            <div className="flex flex-col items-center justify-center gap-3">
                                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-300">
                                                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <p className="font-bold text-slate-700 text-base">ไม่พบข้อมูลที่ค้นหา</p>
                                                    <p className="text-xs text-slate-400 mt-1">ลองเปลี่ยนเงื่อนไขการค้นหา</p>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    currentVehicles.map(v => (
                                        <tr key={v.id} className="hover:bg-slate-50/80 transition-colors group border-b border-slate-50 last:border-0">
                                            <td className="px-1 py-1 text-center text-[10px] font-bold text-slate-700">
                                                {v.sticker_year || '-'}
                                            </td>
                                            <td className="px-1 py-1 text-[10px] font-bold text-slate-700 text-center uppercase">
                                                {v.vehicle_type === 'Car' ? 'รถยนต์' : (v.vehicle_type === 'Motorcycle' ? 'จยย.' : v.vehicle_type)}
                                            </td>
                                            <td className="px-1 py-1 text-[10px] font-bold text-slate-700 text-center tracking-tighter uppercase whitespace-nowrap">
                                                {v.sticker_id || '-'}
                                            </td>
                                            <td className="px-1 py-1 text-center">
                                                <div className="flex justify-center">
                                                    <span className="inline-flex items-center justify-center bg-blue-50/50 text-blue-700 font-bold px-1.5 py-0.5 rounded-lg border border-blue-100/50 shadow-sm text-[10px] tracking-tight whitespace-nowrap  group-hover:bg-blue-100/50">
                                                        {v.sticker_number || '-'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-1 py-1 text-center">
                                                {v.plate_category === 'NoPlate' ? (
                                                    <span className="text-slate-300 font-bold text-[9px] uppercase tracking-widest text-center">ไม่มีทะเบียน</span>
                                                ) : (
                                                    <div className={`px-1 py-0.5 rounded-md border inline-flex flex-col items-center min-w-[55px] shadow-sm transition-colors ${v.plate_category === 'RedPlate' ? 'bg-rose-600 border-rose-700' : 'bg-white border-slate-300'}`}>
                                                        <div className={`font-bold text-[10px] leading-none mb-0.5 ${v.plate_category === 'RedPlate' ? 'text-white' : 'text-slate-800'}`}>{v.license_plate}</div>
                                                        <div className={`text-[8px] font-medium leading-none ${v.plate_category === 'RedPlate' ? 'text-rose-100' : 'text-slate-400'}`}>{v.province}</div>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-1 py-1 text-left font-bold text-slate-700 text-[10px] tracking-tight">
                                                {v.room_number}
                                            </td>
                                            <td className="px-1 py-1 text-left text-[10px] font-bold text-slate-700 whitespace-nowrap">
                                                {v.phone_number || '-'}
                                            </td>
                                            <td className="px-1 py-1">
                                                <div className="font-bold text-slate-700 text-[10px] max-w-[110px] truncate" title={v.name}>{v.name}</div>
                                            </td>
                                            <td className="px-1 py-1">
                                                <div className="text-[10px] text-slate-400 max-w-[120px] truncate" title={`${v.brand || ''} ${v.color || ''}`}>
                                                    {v.brand || '-'} {v.color ? `(${v.color})` : ''}
                                                </div>
                                            </td>
                                            <td className="px-1 py-1 text-left">
                                                <div className="text-[10px] font-xs text-slate-400 truncate max-w-[100px]" title={v.remark}>
                                                    {v.remark || '-'}
                                                </div>
                                            </td>
                                            <td className="px-1 py-1 text-left whitespace-nowrap text-slate-700">
                                                <div className="text-[9px] font-bold leading-none">
                                                    {v.created_at ? new Date(v.created_at).toLocaleString('th-TH', {
                                                        day: 'numeric',
                                                        month: 'numeric',
                                                        year: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: false
                                                    }).replace(',', '') : '-'}
                                                </div>
                                                <div className="text-[8px] text-slate-400 mt-0.5 font-medium truncate max-w-[80px]">by {v.created_by_name || 'System'}</div>
                                            </td>
                                            <td className="px-1 py-1 text-left whitespace-nowrap">
                                                <div className="text-[9px] font-bold text-slate-700 leading-none">
                                                    {v.updated_at ? new Date(v.updated_at).toLocaleString('th-TH', {
                                                        day: 'numeric',
                                                        month: 'numeric',
                                                        year: '2-digit',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        hour12: false
                                                    }).replace(',', '') : '-'}
                                                </div>
                                                <div className="text-[8px] font-medium text-slate-400 mt-0.5 truncate max-w-[80px]">by {v.updated_by_name || 'System'}</div>
                                            </td>
                                            <td className="px-1 py-1 text-center">
                                                <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold tracking-wide border ${(v.is_active === 1 || v.is_active === true)
                                                    ? 'bg-green-50 text-green-700 border-green-200'
                                                    : 'bg-red-50 text-red-700 border-red-200'
                                                    }`}>
                                                    {(v.is_active === 1 || v.is_active === true) ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-1 py-1 text-right">
                                                <div className="flex justify-end gap-0.5">
                                                    {canEdit && (
                                                        <>
                                                            <div className="flex justify-end items-center gap-1.5 pr-1">
                                                                <button
                                                                    onClick={() => openVehicleModal(v)}
                                                                    className="text-slate-300 hover:text-blue-500 transition-colors active:scale-95"
                                                                    title="แก้ไข"
                                                                >
                                                                    <Pencil size={13} strokeWidth={2.5} />
                                                                </button>
                                                                <div className="w-[1px] h-2.5 bg-slate-100" />
                                                                <button
                                                                    onClick={() => handleDeleteVehicle(v.id)}
                                                                    className="text-slate-300 hover:text-rose-500 transition-colors active:scale-95"
                                                                    title="ลบ"
                                                                >
                                                                    <Trash2 size={13} strokeWidth={2.5} />
                                                                </button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}

                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalItems > 0 && (
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">แสดงรายการ:</span>
                                <div className="relative group">
                                    <button
                                        type="button"
                                        onClick={() => setIsRowsDropdownOpen(!isRowsDropdownOpen)}
                                        className="bg-white border border-slate-200 rounded-lg px-2 py-1 min-w-[55px] flex items-center justify-between gap-1.5 shadow-sm hover:border-slate-300 hover:bg-slate-50  outline-none"
                                    >
                                        <span className="text-[12px] font-bold text-slate-700">{rowsPerPage}</span>
                                        <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 ${isRowsDropdownOpen ? 'rotate-180 text-blue-500' : ''}`} />
                                    </button>

                                    {isRowsDropdownOpen && (
                                        <>
                                            <div className="fixed inset-0 z-40" onClick={() => setIsRowsDropdownOpen(false)} />
                                            <div className="absolute bottom-full left-0 mb-2 min-w-full w-max bg-white border border-slate-100 rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-bottom-2 duration-200">
                                                <div className="py-0.5">
                                                    {[10, 20, 50, 100].map(size => (
                                                        <button
                                                            key={size}
                                                            onClick={() => {
                                                                setRowsPerPage(size);
                                                                setIsRowsDropdownOpen(false);
                                                                setCurrentPage(1);
                                                            }}
                                                            className={`w-full px-3 py-1.5 text-[12px] font-bold flex items-center justify-between transition-colors ${rowsPerPage === size ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                        >
                                                            <span>{size}</span>
                                                            {rowsPerPage === size && <Check className="w-3 h-3" />}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider ml-2">
                                    {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, totalItems)} จาก {totalItems}
                                </span>
                            </div>

                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                    className={`p-1.5 rounded-lg  ${currentPage === 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="First Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                                </button>
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className={`p-1.5 rounded-lg  ${currentPage === 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="Previous Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
                                </button>

                                <div className="flex items-center gap-1 px-2">
                                    {/* Show limited page numbers */}
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) pageNum = i + 1;
                                        else if (currentPage <= 3) pageNum = i + 1;
                                        else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                        else pageNum = currentPage - 2 + i;

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-black  ${currentPage === pageNum ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className={`p-1.5 rounded-lg  ${currentPage === totalPages ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="Next Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
                                </button>
                                <button
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className={`p-1.5 rounded-lg  ${currentPage === totalPages ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-slate-100 hover:text-blue-600 active:scale-95'}`}
                                    title="Last Page"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Vehicle Modal */}
            {isVehicleModalOpen && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={closeVehicleModal}
                    />
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col z-10 animate-in zoom-in-95 fade-in duration-200 border border-slate-200">
                        {/* Header */}
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                            <div>
                                <h2 className="text-sm font-bold text-slate-800 tracking-tight">
                                    {editingVehicleId ? 'แก้ไขข้อมูลรถ/สติ๊กเกอร์' : 'เพิ่มรถ/สติ๊กเกอร์ใหม่'}
                                </h2>
                                <p className="text-xs font-medium text-slate-400 mt-0.5">กรอกข้อมูลรายละเอียดรถและเจ้าของ</p>
                            </div>
                            <button
                                type="button"
                                onClick={closeVehicleModal}
                                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 "
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSaveVehicle} className="flex flex-col flex-1 overflow-hidden">
                            <div className="p-4 space-y-1 overflow-y-auto flex-1">

                                {/* Section 1: Sticker Info */}
                                <div className="space-y-1 mb-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-1 h-3 bg-blue-500 rounded-full"></div>
                                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">ข้อมูลสติ๊กเกอร์</h3>
                                    </div>
                                    <div className={`px-3 py-1.5 border rounded-lg mb-2 flex items-start gap-2 ${editingVehicleId ? 'bg-rose-50/50 border-rose-100' : 'bg-amber-50/50 border-amber-100'}`}>
                                        <AlertCircle className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${editingVehicleId ? 'text-rose-500' : 'text-amber-500'}`} />
                                        <p className={`text-[10px] font-medium leading-relaxed ${editingVehicleId ? 'text-rose-600' : 'text-amber-700'}`}>
                                            {editingVehicleId ? (
                                                <>ปีสติ๊กเกอร์, ประเภทรถ, Sticker ID และบ้านเลขที่ ไม่สามารถแก้ไขได้ หากต้องการแก้ไข กรุณาเปลี่ยนสถานะรายการนี้เป็น Inactive และเพิ่มข้อมูลใหม่</>
                                            ) : (
                                                <>โปรดตรวจสอบ ปีสติ๊กเกอร์, ประเภทรถ, Sticker ID และบ้านเลขที่ ให้ถูกต้อง เนื่องจากข้อมูลเหล่านี้จะไม่สามารถแก้ไขได้ในภายหลัง</>
                                            )}
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-2.5">
                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">ปีสติ๊กเกอร์ <span className="text-red-500">*</span></label>
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    disabled={!!editingVehicleId}
                                                    onClick={() => setIsYearOpen(!isYearOpen)}
                                                    className={`w-full h-9 border rounded-lg shadow-sm flex items-center px-3 py-1.5 gap-1.5 text-left ${!!editingVehicleId ? 'bg-slate-100 border-slate-200 cursor-not-allowed' : `bg-white ${isYearOpen ? 'border-slate-300 outline-none' : (error && !vehicleForm.sticker_year ? 'border-rose-500' : 'border-slate-200 hover:border-slate-300')}`}`}
                                                >
                                                    <span className={`text-base md:text-[13px] font-medium truncate flex-1 ${!!editingVehicleId ? 'text-slate-900' : 'text-slate-700'}`}>
                                                        {vehicleForm.sticker_year || 'เลือกปี'}
                                                    </span>
                                                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isYearOpen ? 'rotate-180 text-blue-500' : ''}`} />
                                                </button>
                                                {isYearOpen && !editingVehicleId && (
                                                    <>
                                                        <div className="fixed inset-0 z-[65]" onClick={() => setIsYearOpen(false)} />
                                                        <div className="absolute top-full left-0 mt-1.5 w-full bg-white border border-slate-100 rounded-2xl shadow-2xl z-[70] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200">
                                                            <div className="py-1">
                                                                {[new Date().getFullYear(), new Date().getFullYear() - 1].map(year => (
                                                                    <button
                                                                        key={year}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setVehicleForm({
                                                                                ...vehicleForm,
                                                                                sticker_year: year,
                                                                                sticker_number: ''
                                                                            });
                                                                            setIsYearOpen(false);
                                                                            setError('');
                                                                        }}
                                                                        className={`w-full px-4 py-2 text-left flex items-center justify-between transition-colors ${Number(vehicleForm.sticker_year) === Number(year) ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                    >
                                                                        <span className="text-base md:text-[13px] font-medium">{year}</span>
                                                                        {Number(vehicleForm.sticker_year) === Number(year) && <Check className="w-3.5 h-3.5" />}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">ประเภทรถ <span className="text-red-500">*</span></label>
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    disabled={!!editingVehicleId}
                                                    onClick={() => setIsTypeOpen(!isTypeOpen)}
                                                    className={`w-full h-9 border rounded-lg shadow-sm flex items-center px-3 py-1.5 gap-1.5 text-left ${!!editingVehicleId ? 'bg-slate-100 border-slate-200 cursor-not-allowed' : `bg-white ${isTypeOpen ? 'border-slate-300 outline-none' : (error && !vehicleForm.vehicle_type ? 'border-rose-500' : 'border-slate-200 hover:border-slate-300')}`}`}
                                                >
                                                    <span className={`text-base md:text-[13px] font-medium truncate flex-1 ${!!editingVehicleId ? 'text-slate-900' : 'text-slate-700'}`}>
                                                        {vehicleForm.vehicle_type === 'Car' ? 'รถยนต์' : (vehicleForm.vehicle_type === 'Motorcycle' ? 'จักรยานยนต์' : 'เลือกประเภท')}
                                                    </span>
                                                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isTypeOpen ? 'rotate-180 text-blue-500' : ''}`} />
                                                </button>
                                                {isTypeOpen && !editingVehicleId && (
                                                    <>
                                                        <div className="fixed inset-0 z-[65]" onClick={() => setIsTypeOpen(false)} />
                                                        <div className="absolute top-full left-0 mt-1.5 w-full bg-white border border-slate-100 rounded-2xl shadow-2xl z-[70] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200">
                                                            <div className="py-1">
                                                                {[
                                                                    { label: 'รถยนต์', value: 'Car' },
                                                                    { label: 'รถจักรยานยนต์', value: 'Motorcycle' }
                                                                ].map(opt => (
                                                                    <button
                                                                        key={opt.value}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setVehicleForm({
                                                                                ...vehicleForm,
                                                                                vehicle_type: opt.value,
                                                                                sticker_number: ''
                                                                            });
                                                                            setIsTypeOpen(false);
                                                                            setError('');
                                                                        }}
                                                                        className={`w-full px-4 py-2 text-left flex items-center justify-between transition-colors ${vehicleForm.vehicle_type === opt.value ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                    >
                                                                        <span className="text-base md:text-[13px] font-medium">{opt.label}</span>
                                                                        {vehicleForm.vehicle_type === opt.value && <Check className="w-3.5 h-3.5" />}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 tracking-wider">Sticker ID <span className="text-red-500">*</span></label>
                                            <input
                                                readOnly={!!editingVehicleId}
                                                className={`w-full h-9 text-base md:text-[13px] font-medium text-slate-700 px-3 py-1.5 rounded-lg border outline-none focus:ring-0 focus:border-slate-300 placeholder:text-slate-300 ${!!editingVehicleId ? 'bg-slate-100 text-slate-900 cursor-not-allowed border-slate-200' : `bg-white ${error && !vehicleForm.sticker_id ? 'border-rose-500' : 'border-slate-200'}`}`}
                                                value={vehicleForm.sticker_id || ''}
                                                onChange={e => {
                                                    if (!editingVehicleId) {
                                                        const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                                                        setVehicleForm({
                                                            ...vehicleForm,
                                                            sticker_id: val,
                                                            sticker_number: '' // Clear generated number on change
                                                        });
                                                        setError('');
                                                    }
                                                }}
                                                placeholder="001-999"
                                            />
                                        </div>

                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">บ้านเลขที่ <span className="text-red-500">*</span></label>
                                            <input
                                                readOnly={!!editingVehicleId}
                                                className={`w-full h-9 text-base md:text-[13px] font-medium text-slate-700 px-3 py-1.5 rounded-lg border outline-none focus:ring-0 focus:border-slate-300 placeholder:text-slate-300 ${!!editingVehicleId ? 'bg-slate-100 text-slate-900 cursor-not-allowed border-slate-200' : `bg-white ${error && (!vehicleForm.room_number || vehicleForm.room_number === '234/' || !vehicleForm.room_number.startsWith('234/')) ? 'border-rose-500' : 'border-slate-200'}`}`}
                                                value={vehicleForm.room_number || ''}
                                                onChange={e => {
                                                    if (!editingVehicleId) {
                                                        setVehicleForm({
                                                            ...vehicleForm,
                                                            room_number: e.target.value,
                                                            sticker_number: '' // Clear generated number on change
                                                        });
                                                        setError('');
                                                    }
                                                }}
                                                placeholder="เช่น 234/001"
                                            />
                                        </div>

                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 tracking-wider">Sticker No. <span className="text-red-500">*</span></label>
                                            <div className="flex gap-1.5">
                                                <input
                                                    className={`w-full h-9 text-base md:text-[13px] font-medium px-3 py-1.5 rounded-lg border outline-none focus:ring-0 focus:border-slate-300 tracking-tighter ${!!editingVehicleId ? 'text-slate-900 bg-slate-100 cursor-not-allowed border-slate-200' : `text-slate-700 bg-white ${error && !isGenError && !vehicleForm.sticker_number ? 'border-rose-500' : 'border-slate-200'}`}`}
                                                    value={vehicleForm.sticker_number || ''}
                                                    readOnly={!!editingVehicleId}
                                                    onChange={e => {
                                                        if (!editingVehicleId) {
                                                            const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                                                            setVehicleForm({
                                                                ...vehicleForm,
                                                                sticker_number: val
                                                            });
                                                            setError('');
                                                        }
                                                    }}
                                                    placeholder="12345678"
                                                />
                                                {!editingVehicleId && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const { sticker_year, vehicle_type, sticker_id, room_number } = vehicleForm;
                                                            if (!sticker_year || !vehicle_type || !sticker_id || !room_number) {
                                                                setError('กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วนก่อนกด Gen Sticker No.');
                                                                return;
                                                            }
                                                            if (room_number === '234/' || !room_number.startsWith('234/')) {
                                                                setError('กรุณากรอกเลขที่บ้านต่อจาก 234/ ให้ครบถ้วน');
                                                                return;
                                                            }
                                                            setError('');
                                                            const key = 234;
                                                            const roomPart = room_number.includes('/') ? room_number.split('/').pop() : room_number;
                                                            const roomNum = parseInt(roomPart.replace(/\D/g, '') || '0', 10);
                                                            const hashedRoom = ((roomNum * key) + roomNum).toString().slice(-1);
                                                            const yy = sticker_year.toString().slice(-2);
                                                            const sid = sticker_id.toString().padStart(3, '0');
                                                            const vType = vehicle_type === 'Car' ? '1' : '2';
                                                            const generated = `${sid[0]}${sid[1]}${hashedRoom}${vType}${sid[2]}${yy}`;
                                                            const checksum = generated.split('').reduce((a, b) => a + parseInt(b), 0) % 10;
                                                            setVehicleForm({ ...vehicleForm, sticker_number: generated + checksum });
                                                        }}
                                                        className="h-9 font-bold bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-200 border border-slate-200 text-[10px] active:scale-95 whitespace-nowrap focus:outline-none"
                                                    >
                                                        Gen
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">สถานะ</label>
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsStatusOpen(!isStatusOpen)}
                                                    className={`w-full h-9 bg-white border rounded-lg shadow-sm flex items-center px-3 py-1.5 gap-1.5 text-left ${isStatusOpen ? 'border-slate-300 outline-none' : 'border-slate-200 hover:border-slate-300'}`}
                                                >
                                                    <span className={`text-base md:text-[13px] font-medium truncate flex-1 ${vehicleForm.is_active ? 'text-blue-600' : 'text-slate-500'}`}>{vehicleForm.is_active ? 'Active' : 'Inactive'}</span>
                                                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isStatusOpen ? 'rotate-180 text-blue-500' : 'text-slate-400'}`} />
                                                </button>
                                                {isStatusOpen && (
                                                    <>
                                                        <div className="fixed inset-0 z-[65]" onClick={() => setIsStatusOpen(false)} />
                                                        <div className="absolute top-full left-0 mt-1.5 w-full bg-white border border-slate-100 rounded-2xl shadow-2xl z-[70] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200">
                                                            <div className="py-1">
                                                                {[
                                                                    { label: 'Active', value: true },
                                                                    { label: 'Inactive', value: false }
                                                                ].map(opt => (
                                                                    <button
                                                                        key={opt.label}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setVehicleForm({ ...vehicleForm, is_active: opt.value });
                                                                            setIsStatusOpen(false);
                                                                            setError('');
                                                                        }}
                                                                        className={`w-full px-4 py-2 text-left flex items-center justify-between transition-colors ${vehicleForm.is_active === opt.value ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                    >
                                                                        <span className="text-base md:text-[13px] font-medium">{opt.label}</span>
                                                                        {vehicleForm.is_active === opt.value && <Check className="w-3.5 h-3.5" />}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Section 2: Resident Info */}
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-1 h-3 bg-indigo-500 rounded-full"></div>
                                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">ข้อมูลผู้พักอาศัย</h3>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2.5">
                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">ชื่อผู้พักอาศัย <span className="text-red-500">*</span></label>
                                            <input
                                                className={`w-full h-9 text-base md:text-[13px] font-medium text-slate-700 px-3 py-1.5 rounded-lg border outline-none focus:ring-0 focus:border-slate-300 placeholder:text-slate-300 ${error && !isGenError && !vehicleForm.name ? 'border-rose-500' : 'border-slate-200'}`}
                                                value={vehicleForm.name || ''}
                                                onChange={e => { setVehicleForm({ ...vehicleForm, name: e.target.value }); setError(''); }}
                                                placeholder="ชื่อ-นามสกุล"
                                            />
                                        </div>
                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">เบอร์โทรศัพท์ <span className="text-red-500">*</span></label>
                                            <input
                                                className={`w-full h-9 text-base md:text-[13px] font-medium text-slate-700 px-3 py-1.5 rounded-lg border outline-none focus:ring-0 focus:border-slate-300 placeholder:text-slate-300 ${(error && !isGenError && !vehicleForm.phone_number) || (error === 'เบอร์โทรศัพท์ต้องมี 10 หลัก') ? 'border-rose-500' : 'border-slate-200'}`}
                                                value={vehicleForm.phone_number || ''}
                                                onChange={e => {
                                                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                                    setVehicleForm({ ...vehicleForm, phone_number: val });
                                                    setError('');
                                                }}
                                                placeholder="0812345678"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Section 3: Vehicle Info */}
                                <div className="space-y-1 pt-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-1 h-3 bg-emerald-500 rounded-full"></div>
                                        <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">ข้อมูลรถ</h3>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-2.5">
                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">ประเภทป้าย</label>
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsPlateCategoryOpen(!isPlateCategoryOpen)}
                                                    className={`w-full h-9 bg-white border border-slate-200 rounded-lg shadow-sm flex items-center px-3 py-1.5 gap-1.5 text-left ${isPlateCategoryOpen ? 'border-slate-300 outline-none' : 'hover:border-slate-300'}`}
                                                >
                                                    <span className="text-base md:text-[13px] font-medium text-slate-700 truncate flex-1">
                                                        {vehicleForm.plate_category === 'Normal' ? 'ป้ายขาว' : (vehicleForm.plate_category === 'RedPlate' ? 'ป้ายแดง' : 'ไม่มีทะเบียน')}
                                                    </span>
                                                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isPlateCategoryOpen ? 'rotate-180 text-blue-500' : ''}`} />
                                                </button>
                                                {isPlateCategoryOpen && (
                                                    <>
                                                        <div className="fixed inset-0 z-[65]" onClick={() => setIsPlateCategoryOpen(false)} />
                                                        <div className="absolute top-full left-0 mt-1.5 w-full bg-white border border-slate-100 rounded-2xl shadow-2xl z-[70] overflow-hidden animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200">
                                                            <div className="py-1">
                                                                {[
                                                                    { label: 'ป้ายขาว', value: 'Normal' },
                                                                    { label: 'ป้ายแดง', value: 'RedPlate' },
                                                                    { label: 'ยังไม่มีทะเบียน', value: 'NoPlate' }
                                                                ].map(opt => (
                                                                    <button
                                                                        key={opt.value}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            let newLicensePlate = vehicleForm.license_plate;
                                                                            if (opt.value === 'NoPlate') {
                                                                                newLicensePlate = '';
                                                                            } else if (newLicensePlate === 'ยังไม่มีทะเบียน' || newLicensePlate === 'ป้ายแดง') {
                                                                                newLicensePlate = '';
                                                                            }

                                                                            setVehicleForm({
                                                                                ...vehicleForm,
                                                                                plate_category: opt.value,
                                                                                license_plate: newLicensePlate,
                                                                                province: opt.value === 'NoPlate' ? '' : vehicleForm.province
                                                                            });
                                                                            if (opt.value === 'NoPlate') setProvinceSearch('');
                                                                            setIsPlateCategoryOpen(false);
                                                                        }}
                                                                        className={`w-full px-4 py-2 text-left flex items-center justify-between transition-colors ${vehicleForm.plate_category === opt.value ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                                                                    >
                                                                        <span className="text-base md:text-[13px] font-medium">{opt.label}</span>
                                                                        {vehicleForm.plate_category === opt.value && <Check className="w-3.5 h-3.5" />}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">ทะเบียนรถ {(vehicleForm.plate_category === 'Normal' || vehicleForm.plate_category === 'RedPlate') && <span className="text-red-500">*</span>}</label>
                                            <input
                                                disabled={vehicleForm.plate_category === 'NoPlate'}
                                                className={`w-full h-9 text-base md:text-[13px] font-medium px-3 py-1.5 rounded-lg border outline-none focus:ring-0 focus:border-slate-300 placeholder:text-slate-300 ${vehicleForm.plate_category === 'NoPlate' ? 'bg-slate-100 text-slate-900 cursor-not-allowed border-slate-200' : `bg-white text-slate-700 ${error && !isGenError && (vehicleForm.plate_category === 'Normal' || vehicleForm.plate_category === 'RedPlate') && !vehicleForm.license_plate ? 'border-rose-500' : 'border-slate-200'}`}`}
                                                value={vehicleForm.plate_category === 'NoPlate' ? '-' : (vehicleForm.license_plate || '')}
                                                onChange={e => {
                                                    const val = e.target.value.replace(/[^ก-ฮ0-9ก-ฮ]/g, ''); // Allow Thai characters and numbers
                                                    setVehicleForm({ ...vehicleForm, license_plate: val });
                                                    setError('');
                                                }}
                                                placeholder={vehicleForm.plate_category === 'Normal' ? '2กก1234' : '2กก1234'}
                                            />
                                        </div>

                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">จังหวัด {(vehicleForm.plate_category === 'Normal' || vehicleForm.plate_category === 'RedPlate') && <span className="text-red-500">*</span>}</label>
                                            <div className="relative">
                                                <input
                                                    ref={provinceInputRef}
                                                    type="text"
                                                    disabled={vehicleForm.plate_category === 'NoPlate'}
                                                    className={`w-full h-9 text-base md:text-[13px] font-medium px-3 py-1.5 rounded-lg border outline-none focus:ring-0 focus:border-slate-300 placeholder:text-slate-300 ${vehicleForm.plate_category === 'NoPlate' ? 'bg-slate-100 text-slate-900 cursor-not-allowed border-slate-200' : `bg-white text-slate-700 ${error && !isGenError && (vehicleForm.plate_category === 'Normal' || vehicleForm.plate_category === 'RedPlate') && !vehicleForm.province ? 'border-rose-500' : 'border-slate-200'}`}`}
                                                    placeholder="เลือกจังหวัด"
                                                    value={vehicleForm.plate_category === 'NoPlate' ? '-' : (provinceSearch || '')}
                                                    onChange={(e) => {
                                                        setProvinceSearch(e.target.value);
                                                        setIsProvinceOpen(true);
                                                        updateDropdownPosition();
                                                        setVehicleForm({ ...vehicleForm, province: e.target.value });
                                                    }}
                                                    onFocus={() => {
                                                        setIsProvinceOpen(true);
                                                        updateDropdownPosition();
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            const trimmed = provinceSearch ? provinceSearch.trim() : '';
                                                            const matches = allProvinces.filter(p => p.includes(trimmed));
                                                            if (matches.length > 0) {
                                                                setVehicleForm(prev => ({ ...prev, province: matches[0] }));
                                                                setProvinceSearch(matches[0]);
                                                                setIsProvinceOpen(false);
                                                            }
                                                        }
                                                    }}
                                                />
                                                {isProvinceOpen && (
                                                    <>
                                                        <div className="fixed inset-0 z-[9998] bg-transparent" onClick={handleCloseProvinceDropdown}></div>
                                                        <div className="fixed bg-white border border-slate-100 rounded-2xl shadow-2xl overflow-y-auto animate-in zoom-in-95 duration-200" style={dropdownStyle}>
                                                            {allProvinces.filter(p => p.includes(provinceSearch)).length > 0 ? (
                                                                allProvinces.filter(p => p.includes(provinceSearch)).map(p => (
                                                                    <button
                                                                        key={p}
                                                                        type="button"
                                                                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 text-slate-700 font-medium transition-colors"
                                                                        onClick={() => {
                                                                            setVehicleForm({ ...vehicleForm, province: p });
                                                                            setProvinceSearch(p);
                                                                            setIsProvinceOpen(false);
                                                                        }}
                                                                    >
                                                                        {p}
                                                                    </button>
                                                                ))
                                                            ) : (
                                                                <div className="px-3 py-2 text-xs text-slate-400">ไม่พบข้อมูล</div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-span-1 sm:col-span-2 space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">ยี่ห้อ/รุ่น</label>
                                            <input
                                                className="w-full h-9 text-base md:text-[13px] font-medium text-slate-700 px-3 py-1.5 rounded-lg border border-slate-200 outline-none focus:ring-0 focus:border-slate-300  placeholder:text-slate-300"
                                                value={vehicleForm.brand || ''}
                                                onChange={e => setVehicleForm({ ...vehicleForm, brand: e.target.value })}
                                                placeholder="เช่น Toyota Camry"
                                            />
                                        </div>
                                        <div className="space-y-0.5">
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">สี</label>
                                            <input
                                                className="w-full h-9 text-base md:text-[13px] font-medium text-slate-700 px-3 py-1.5 rounded-lg border border-slate-200 outline-none focus:ring-0 focus:border-slate-300  placeholder:text-slate-300"
                                                value={vehicleForm.color || ''}
                                                onChange={e => setVehicleForm({ ...vehicleForm, color: e.target.value })}
                                                placeholder="เช่น สีขาว"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Row 4: Remark */}
                                <div className="space-y-1 pb-2">
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">หมายเหตุ</label>
                                    <textarea
                                        className="w-full text-base md:text-[13px] font-medium text-slate-700 px-3 py-1.5 rounded-lg border border-slate-200 outline-none focus:ring-0 focus:border-slate-300  placeholder:text-slate-300 min-h-[40px] max-h-[100px]"
                                        value={vehicleForm.remark || ''}
                                        onChange={e => { setVehicleForm({ ...vehicleForm, remark: e.target.value }); setError(''); }}
                                        placeholder="ระบุหมายเหตุเพิ่มเติม..."
                                    />
                                </div>

                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="mx-5 mb-4 p-3 bg-rose-50 border border-rose-100 rounded-xl flex items-center gap-3 text-rose-600 animate-in slide-in-from-bottom-2 duration-200">
                                    <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                                        <AlertTriangle size={18} strokeWidth={2.5} />
                                    </div>
                                    <span className="text-xs">{error}</span>
                                </div>
                            )}

                            {/* Footer */}
                            <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex gap-3 shrink-0">
                                <button
                                    type="button"
                                    onClick={closeVehicleModal}
                                    className="flex-1 px-4 py-2.5 rounded-xl text-slate-600 text-sm font-bold hover:bg-slate-100  border border-slate-200 active:scale-95"
                                >
                                    ยกเลิก
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-500/25 active:scale-95 "
                                >
                                    {editingVehicleId ? 'บันทึกแก้ไข' : 'บันทึก'}
                                </button>
                            </div>
                        </form>
                    </div >
                </div >
            )
            }

            {/* Custom Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowDeleteConfirm(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[340px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-rose-50 rounded-full flex items-center justify-center mb-6">
                                <Trash2 className="w-8 h-8 text-rose-500" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800 tracking-tight">ยืนยันการลบข้อมูล?</h3>
                            <p className="text-sm font-medium text-slate-400 mt-2">ข้อมูลรถและสติ๊กเกอร์จะถูกลบถาวร</p>

                            <div className="flex gap-3 w-full mt-10">
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    className="flex-1 py-2 border border-slate-100 text-slate-500 rounded-2xl font-bold text-[14px]  active:scale-95 hover:bg-slate-50"
                                >
                                    ยกเลิก
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="flex-1 py-2 bg-rose-500 text-white rounded-2xl font-bold text-[14px] shadow-lg shadow-rose-100  active:scale-95 hover:bg-rose-600 uppercase tracking-wide"
                                >
                                    ลบข้อมูล
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Resident Modal (Quick Add) */}

            {/* Success Popup Modal */}
            {showSuccessPopup && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/70 animate-in fade-in duration-300"
                        onClick={() => setShowSuccessPopup(false)}
                    />
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-[320px] overflow-hidden z-20 animate-in zoom-in-95 fade-in duration-300 border border-slate-100">
                        <div className="p-10 flex flex-col items-center text-center">
                            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-6">
                                <Check className="w-8 h-8 text-emerald-500" />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800 tracking-tight">ทำรายการสำเร็จ</h3>
                            <p className="text-sm font-medium text-slate-400 mt-2">{successMessage}</p>

                            <button
                                onClick={() => setShowSuccessPopup(false)}
                                className="mt-8 w-full py-2 bg-blue-600 text-white rounded-2xl font-bold text-[14px] tracking-wide  active:scale-95 hover:bg-blue-700 shadow-lg shadow-blue-100"
                            >
                                ตกลง
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResidentManagement;
